import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { useTrip, useDeleteTrip } from '../hooks/useTravelBuddy';
import { ReceiptsTab } from './ReceiptsTab';
import { RoutesTab } from './RoutesTab';
import { PackingTab } from './PackingTab';
import { TodoTab } from './TodoTab';
import { SummaryTab } from './SummaryTab';
import * as T from '../ui/inlineTheme';

type Tab = 'receipts' | 'routes' | 'packing' | 'todo' | 'summary';

export function TripDetail() {
  const { tripId } = useParams<{ tripId: string }>();
  const navigate = useNavigate();
  const { data: trip, isLoading, isError } = useTrip(tripId);
  const deleteTrip = useDeleteTrip();
  const [tab, setTab] = useState<Tab>('receipts');

  const onDeleteThisTrip = () => {
    if (!tripId || !trip) return;
    if (
      !window.confirm(
        `Delete trip “${trip.name}”? This removes all receipts, routes, packing lists, and to-do lists for this trip. This cannot be undone.`
      )
    ) {
      return;
    }
    deleteTrip.mutate(tripId, {
      onSuccess: () => navigate('/travel-buddy', { replace: true }),
    });
  };

  if (!tripId) return null;

  if (isLoading) {
    return (
      <div style={T.page}>
        <p style={T.bodyMuted}>Loading trip…</p>
      </div>
    );
  }

  if (isError || !trip) {
    return (
      <div style={T.page}>
        <p style={{ ...T.bodyMuted, color: '#f87171' }}>Trip not found.</p>
        <Link to="/travel-buddy" style={{ ...T.linkBack, marginTop: 12 }}>
          Back to trips
        </Link>
      </div>
    );
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: 'receipts', label: 'Receipts' },
    { id: 'routes', label: 'Routes' },
    { id: 'packing', label: 'Packing' },
    { id: 'todo', label: 'To-do' },
    { id: 'summary', label: 'Summary' },
  ];

  return (
    <div style={{ ...T.page, paddingTop: 16 }}>
      <Link to="/travel-buddy" style={T.linkBack}>
        <ArrowLeft size={18} />
        All trips
      </Link>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <h1 style={{ ...T.h1, fontSize: 'clamp(1.35rem, 4vw, 1.6rem)', marginBottom: 6 }}>{trip.name}</h1>
          <p style={{ margin: 0, fontFamily: T.font, fontSize: 14, color: '#9CA3AF' }}>
            Created {new Date(trip.created_at).toLocaleString()}
          </p>
        </div>
        <button
          type="button"
          onClick={onDeleteThisTrip}
          disabled={deleteTrip.isPending}
          style={{
            ...T.btnSecondary,
            flexShrink: 0,
            borderColor: 'rgba(153, 27, 27, 0.6)',
            backgroundColor: 'rgba(69, 10, 10, 0.45)',
            color: '#fecaca',
            opacity: deleteTrip.isPending ? 0.65 : 1,
          }}
        >
          <Trash2 size={18} style={{ marginRight: 8 }} aria-hidden />
          Delete trip
        </button>
      </div>

      <div className="tb-tab-bar" style={T.tabBar} role="tablist">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            style={T.tabBtn(tab === t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ marginTop: 22 }} role="tabpanel">
        {tab === 'receipts' && <ReceiptsTab tripId={tripId} />}
        {tab === 'routes' && <RoutesTab tripId={tripId} />}
        {tab === 'packing' && <PackingTab tripId={tripId} />}
        {tab === 'todo' && <TodoTab tripId={tripId} />}
        {tab === 'summary' && <SummaryTab tripId={tripId} />}
      </div>
    </div>
  );
}
